// Configuration de l'URL de base pour l'authentification
// En production, utilise une URL relative, en développement utilise localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api'
);

// Debug: afficher l'URL utilisée pour l'auth (à supprimer en production)
// console.log('AuthService API_BASE_URL:', API_BASE_URL);

export interface User {
  id: string;
  username: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

export interface LoginResponse {
  message: string;
  user: User;
  token: string;
}

export interface AuthError {
  error: string;
  errors?: Array<{ msg: string; param: string }>;
}

class AuthService {
  private token: string | null = null;
  private user: User | null = null;

  constructor() {
    // Récupérer le token depuis le localStorage au démarrage
    this.token = localStorage.getItem('ccgestion_token');
    const userStr = localStorage.getItem('ccgestion_user');
    if (userStr) {
      try {
        this.user = JSON.parse(userStr);
      } catch (error) {
        console.error('Erreur lors du parsing des données utilisateur:', error);
        this.logout();
      }
    }
  }

  // Connexion
  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur de connexion');
      }

      // Stocker le token et les informations utilisateur
      this.token = data.token;
      this.user = data.user;
      
      if (this.token) {
        localStorage.setItem('ccgestion_token', this.token);
      }
      if (this.user) {
        localStorage.setItem('ccgestion_user', JSON.stringify(this.user));
      }

      return data;
    } catch (error) {
      console.error('Erreur de connexion:', error);
      throw error;
    }
  }

  // Déconnexion
  async logout(): Promise<void> {
    // Notifier le serveur de la déconnexion si on a un token
    if (this.token) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        console.error('Erreur lors de la déconnexion côté serveur:', error);
        // Continuer la déconnexion locale même si le serveur échoue
      }
    }

    // Nettoyer les données locales
    this.token = null;
    this.user = null;
    localStorage.removeItem('ccgestion_token');
    localStorage.removeItem('ccgestion_user');
  }

  // Vérifier si l'utilisateur est connecté
  isAuthenticated(): boolean {
    return this.token !== null && this.user !== null;
  }

  // Obtenir le token actuel
  getToken(): string | null {
    return this.token;
  }

  // Obtenir l'utilisateur actuel
  getUser(): User | null {
    return this.user;
  }

  // Vérifier le token auprès du serveur
  async verifyToken(): Promise<User | null> {
    if (!this.token) {
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });

      if (!response.ok) {
        this.logout();
        return null;
      }

      const data = await response.json();
      this.user = data.user;
      localStorage.setItem('ccgestion_user', JSON.stringify(this.user));
      
      return this.user;
    } catch (error) {
      console.error('Erreur lors de la vérification du token:', error);
      this.logout();
      return null;
    }
  }

  // Obtenir les headers d'authentification pour les requêtes API
  getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  // Créer un nouvel utilisateur (admin seulement)
  async createUser(userData: {
    username: string;
    email: string;
    password: string;
    name: string;
    role?: string;
  }): Promise<User> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/users`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la création de l\'utilisateur');
      }

      return data.user;
    } catch (error) {
      console.error('Erreur lors de la création de l\'utilisateur:', error);
      throw error;
    }
  }

  // Obtenir la liste des utilisateurs (admin seulement)
  async getUsers(): Promise<User[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/users`, {
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la récupération des utilisateurs');
      }

      return data.users;
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      throw error;
    }
  }
}

// Instance singleton
const authService = new AuthService();
export default authService; 